
import { jsPDF } from "jspdf";
import { PdfLayoutConfig } from "../types";

/**
 * Adds a placeholder for images that fail to load
 */
export const addImagePlaceholder = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  title: string
) => {
  // Draw placeholder background
  doc.setFillColor(240, 240, 240);
  doc.rect(x, y, width, height, 'F');
  
  // Draw border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, 'S');
  
  // Add placeholder text
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Image unavailable', x + width/2, y + height/2 - 3, { align: 'center' });
  // Limit title length for safety
  const safeTitle = title.substring(0, 30);
  doc.text(safeTitle, x + width/2, y + height/2 + 3, { align: 'center' });
};

/**
 * Calculates layout configuration for the PDF
 */
export const calculateLayoutConfig = (doc: jsPDF): PdfLayoutConfig => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const usableWidth = pageWidth - 2 * margin;
  const imagesPerRow = 2;
  const imageWidth = usableWidth / imagesPerRow;
  const imageHeight = imageWidth * 0.75; // Aspect ratio
  const spacing = 15;
  
  return {
    pageWidth,
    margin,
    usableWidth,
    imagesPerRow,
    imageWidth,
    imageHeight,
    spacing
  };
};

/**
 * Adds page numbers to the PDF
 */
export const addPageNumbers = (doc: jsPDF) => {
  const pageCount = doc.getNumberOfPages();
  for (let j = 1; j <= pageCount; j++) {
    doc.setPage(j);
    doc.setFontSize(8);
    doc.setTextColor(127, 140, 141);
    doc.text(
      `${j} / ${pageCount}`, 
      doc.internal.pageSize.getWidth() - 15, 
      doc.internal.pageSize.getHeight() - 10
    );
  }
};

/**
 * Creates a valid filename from the title
 */
export const createSafeFileName = (title: string | undefined): string => {
  const fileTitle = title || 'gallery';
  return fileTitle
    .replace(/[^a-zA-Z0-9-_]/g, '_') // Replace unsuitable chars with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with a single one
    .toLowerCase();
};

/**
 * Add title to PDF document
 */
export const addPdfTitle = (doc: jsPDF, title: string) => {
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80);
  doc.text(title, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
};

/**
 * Add decorative line under title
 */
export const addDecorativeLine = (doc: jsPDF) => {
  doc.setDrawColor(52, 152, 219);
  doc.setLineWidth(0.5);
  doc.line(20, 25, doc.internal.pageSize.getWidth() - 20, 25);
};
